import { useState } from 'react'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { Box, Button } from '@factorialco/deprecated-design-system'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { useStructuredCostCenterMutations } from 'modules/cost_centers/hooks/useStructuredCostCenterMutations'
import { CostCenterConfigurations, CostCenterDetails } from 'modules/cost_centers/lib/types'

interface Props {
  onClose: () => void
  costCenter: CostCenterDetails
  onSuccess: () => void
  configurations?: CostCenterConfigurations
  hasNewStructureEnabled?: boolean
}

interface FormValues {
  code: string
  description: string
}

export const CostCenterEditModal = ({
  onClose,
  costCenter,
  onSuccess,
  configurations,
  hasNewStructureEnabled,
}: Props) => {
  const [submitting, setSubmitting] = useState<boolean>(false)

  const { save } = useStructuredCostCenterMutations()

  const dimensionValues = useUnsafeMemo(() => {
    const dimensions =
      'dimensions' in costCenter && costCenter.dimensions?.length ? costCenter.dimensions : []

    let familyName: string | null = null
    let functionName: string | null = null
    let roleName: string | null = null
    let workplaceName: string | null = null

    for (const d of dimensions) {
      if (d.jobCatalogFamilyName) familyName = d.jobCatalogFamilyName
      if (d.jobCatalogFunctionName) functionName = d.jobCatalogFunctionName
      if (d.jobCatalogRoleName) roleName = d.jobCatalogRoleName
      if (d.workplaceName) workplaceName = d.workplaceName
    }

    return { familyName, functionName, roleName, workplaceName }
  }, [costCenter])

  const jobCatalogStructure = useUnsafeMemo(() => {
    if (!configurations) return undefined
    const jcConfig = configurations.find((config) => config.structure.startsWith('job_catalog_'))
    return jcConfig?.structure
  }, [configurations])

  const hasWorkplace = useUnsafeMemo(() => {
    const hasConfig = configurations?.some((config) => config.structure === 'workplace') ?? false
    return hasConfig || !!dimensionValues.workplaceName
  }, [configurations, dimensionValues])

  const shouldShowJobCatalogFields = useUnsafeMemo(
    () => ({
      family:
        jobCatalogStructure === 'job_catalog_family' ||
        jobCatalogStructure === 'job_catalog_function' ||
        jobCatalogStructure === 'job_catalog_role',
      function:
        jobCatalogStructure === 'job_catalog_function' ||
        jobCatalogStructure === 'job_catalog_role',
      role: jobCatalogStructure === 'job_catalog_role',
    }),
    [jobCatalogStructure]
  )

  const form = useForm<FormValues>({
    defaultValues: {
      code: costCenter?.code || '',
      description: costCenter?.description || '',
    },

    onSubmit: async (values) => {
      setSubmitting(true)

      const variables = {
        id: costCenter?.id,
        name: costCenter?.name,
        code: values.code,
        description: values.description,
        onSuccess: onSuccess,
        onError: (errors: Record<string, string>) => {
          form.setErrors({
            code: errors['code'],
            description: errors['description'],
          })
        },
      }

      const result = await save(variables)

      setSubmitting(false)
      return result
    },
  })

  return (
    <Modal
      title={i18n.t('finance.cost_centers_structure.edit.title')}
      illustration="document-edit"
      onClose={onClose}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={() => form.handleSubmit()} disabled={submitting}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Box gap="s12">
        {hasNewStructureEnabled ? (
          <>
            {shouldShowJobCatalogFields.family && (
              <TextField
                label={i18n.t('finance.cost_centers_list.headers.job_catalog.family')}
                value={dimensionValues.familyName || '-'}
                readonly
              />
            )}
            {shouldShowJobCatalogFields.function && (
              <TextField
                label={i18n.t('finance.cost_centers_list.headers.job_catalog.function')}
                value={dimensionValues.functionName || '-'}
                readonly
              />
            )}
            {shouldShowJobCatalogFields.role && (
              <TextField
                label={i18n.t('finance.cost_centers_list.headers.job_catalog.role')}
                value={dimensionValues.roleName || '-'}
                readonly
              />
            )}
            {hasWorkplace && (
              <TextField
                label={i18n.t('finance.cost_centers.assignments.structured.workplace')}
                value={dimensionValues.workplaceName || '-'}
                readonly
              />
            )}
          </>
        ) : (
          <TextField
            mandatory
            label={i18n.t('finance.cost_centers_structure.edit.labels.name')}
            value={costCenter.name}
            readonly
          />
        )}
        <TextField
          label={i18n.t('finance.cost_centers_structure.edit.labels.code')}
          value={form.values.code}
          onChange={(value) => form.setValues({ ...form.values, code: value })}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('finance.modals.add_cost_center.fields.code.information')}
              icon="question"
            />
          }
        />
        <SelectField
          label={i18n.t('finance.cost_center_detail.sidepanel.legal_entity')}
          value={
            costCenter.legalEntity?.legalName ??
            i18n.t('finance.cost_centers_list.legal_entity_unassigned')
          }
          options={[
            {
              label:
                costCenter.legalEntity?.legalName ??
                i18n.t('finance.cost_centers_list.legal_entity_unassigned'),
              value:
                costCenter.legalEntity?.legalName ??
                i18n.t('finance.cost_centers_list.legal_entity_unassigned'),
            },
          ]}
          readonly
        />
        <TextField
          label={i18n.t('finance.cost_center_detail.sidepanel.description')}
          value={form.values.description}
          onChange={(value) => form.setValues({ ...form.values, description: value })}
        />
      </Box>
    </Modal>
  )
}
